import ExcelJS from 'exceljs';
import axios from 'axios';

export const xlsxSheets = {
    generateHeaders: headers => {
        // Directly use headers provided as they are already in desired format
        return headers;
    },

    generateData: (items, headers) => {
        return items.map(item => {
            return headers.map(header => {
                let value = item[header.value];
                // Formatting based on header
                if (header.value === 'total_cost') {
                    value = `$${parseFloat(value).toFixed(2)}`; // Format currency
                }
                return value;
            });
        });
    },

    columnToLetter: column => {
        let temp,
            letter = '';
        while (column > 0) {
            temp = (column - 1) % 26;
            letter = String.fromCharCode(temp + 65) + letter;
            column = (column - temp - 1) / 26;
        }
        return letter;
    },

    downloadSheet: async (data, headers, title) => {
        const date = new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/:/g, '-');
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Report');

        // Set the columns using the 'text' property from the headers
        worksheet.columns = headers.map(header => ({
            header: header.text,
            key: header.value,
            width: 20
        }));

        // Add data rows to the worksheet
        data.forEach(row => {
            worksheet.addRow(row);
        });

        // Save the workbook to a buffer and initiate a download
        try {
            // Generate buffer
            const buffer = await workbook.xlsx.writeBuffer();

            // Create Blob for download
            const blob = new Blob([buffer], {
                type:
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${title}_${date}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error writing Excel file:', error);
        }
    },

    generateSheet: (items, type, headers, title) => {
        if (type === 'xlsx') {
            const processedHeaders = xlsxSheets.generateHeaders(headers);
            const data = xlsxSheets.generateData(items, processedHeaders);
            xlsxSheets.downloadSheet(data, processedHeaders, title);
        }
        // Add CSV generation logic if needed
    },

    generatePivotSheet: async (data, headers, title) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Pivot Report');

        const equipmentSet = new Set();
        const categoriesSet = new Set();

        // Gather all unique equipment and categories
        data.forEach(item => {
            equipmentSet.add(item.name);
            categoriesSet.add(item.category_name); // Capture category names correctly
        });

        const equipments = Array.from(equipmentSet);
        const categories = Array.from(categoriesSet);

        // Setup worksheet columns
        worksheet.columns = [
            { header: 'Equipment/Category', key: 'category', width: 25 },
            ...categories.map(category => ({
                header: category,
                key: category,
                width: 15
            })),
            {
                header: 'Total Cost per Equipment',
                key: 'totalCostPerEquipment',
                width: 20
            } // Additional column for total cost per equipment
        ];

        // Fill worksheet rows
        equipments.forEach(equipment => {
            const row = { category: equipment };
            let totalCostPerEquipment = 0; // Initialize the total cost per equipment

            categories.forEach(category => {
                const foundItem = data.find(
                    item =>
                        item.name === equipment &&
                        item.category_name === category
                );
                const cost = foundItem
                    ? parseFloat(foundItem.category_total_cost)
                    : 0;
                row[category] = cost;
                totalCostPerEquipment += cost; // Sum up total cost for this equipment
            });

            row['totalCostPerEquipment'] = totalCostPerEquipment.toFixed(2); // Add total cost to the row
            worksheet.addRow(row);
        });

        // Add total row at the end
        const totalRow = { category: 'Total' };
        categories.forEach(category => {
            totalRow[category] = data
                .filter(item => item.category_name === category)
                .reduce(
                    (acc, curr) => acc + parseFloat(curr.category_total_cost),
                    0
                )
                .toFixed(2);
        });

        // Calculate the grand total for all equipment and categories
        totalRow['totalCostPerEquipment'] = data
            .reduce(
                (acc, curr) => acc + parseFloat(curr.category_total_cost),
                0
            )
            .toFixed(2);

        worksheet.addRow(totalRow);

        // Write workbook to a buffer
        const buffer = await workbook.xlsx.writeBuffer();
        return buffer;
    },

    downloadPivotSheet: async (data, headers, title) => {
        let date = new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/:/g, '-');
        try {
            const buffer = await xlsxSheets.generatePivotSheet(
                data,
                headers,
                title
            );
            const blob = new Blob([buffer], {
                type:
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${title}_${date}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error creating Excel file:', error);
        }
    },

    getAnnualSpendingReport: async data => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Monthly Expenses');

        // Define headers for the main data worksheet
        const headers = [
            { header: 'Trailer', key: 'trailer', width: 20 },
            { header: 'Region', key: 'region', width: 15 },
            ...Array.from({ length: 12 }, (_, i) => ({
                header: new Date(0, i + 1, 0).toLocaleString('default', {
                    month: 'short'
                }),
                key: `${i + 1}`,
                width: 10
            })),
            { header: 'Total', key: 'total', width: 12 }
        ];
        worksheet.columns = headers;

        let trailerMap = new Map();
        let categoryColors = new Map(); // Map to store category colors

        // Process data
        data.forEach(item => {
            const month = new Date(item.invoice_date).getMonth() + 1;
            const trailerEntry = trailerMap.get(item.trailer_name) || {
                trailer: item.trailer_name,
                region: item.region,
                total: 0,
                monthDetails: Array.from({ length: 12 }, () => ({
                    cost: 0,
                    color: null
                }))
            };

            const currentMonthTotal = trailerEntry.monthDetails[month - 1];
            const newCost = parseFloat(item.invoice_total_cost);

            if (newCost > currentMonthTotal.cost) {
                currentMonthTotal.cost = newCost;
                currentMonthTotal.color = item.category_color;
            }

            trailerEntry[month - 1] += newCost;
            trailerEntry.total += newCost;
            trailerMap.set(item.trailer_name, trailerEntry);

            // Update category colors map
            categoryColors.set(item.category_name, item.category_color);
        });

        // Add rows to the worksheet with formatting
        trailerMap.forEach(entry => {
            const rowValues = {
                trailer: entry.trailer,
                region: entry.region,
                ...Object.fromEntries(
                    Array.from({ length: 12 }, (_, i) => [
                        `${i + 1}`,
                        entry.monthDetails[i].cost.toFixed(2)
                    ])
                ),
                total: entry.total.toFixed(2)
            };
            const row = worksheet.addRow(rowValues);
            entry.monthDetails.forEach((detail, index) => {
                if (detail.color) {
                    row.getCell(index + 3).fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: detail.color.replace('#', '') }
                    };
                }
            });
        });

        // Create a new worksheet for the legend
        const legendSheet = workbook.addWorksheet('Legend');
        legendSheet.columns = [
            { header: 'Category', key: 'category', width: 20 },
            { header: 'Color', key: 'color', width: 20 }
        ];

        // Add category and color information to the legend sheet
        categoryColors.forEach((color, category) => {
            legendSheet.addRow({ category: category, color: color });
            legendSheet.getCell(legendSheet.lastRow.number, 2).fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: color.replace('#', '') }
            };
        });

        // Write and download the Excel file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type:
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Monthly_Trailer_Expenses_with_Legend.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    },

    getAnnualSpendingByCategoryReport: async data => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Annual Spending by Category');

        // Extract unique categories from data
        const categories = Array.from(
            new Set(data.map(item => item.category_name))
        );

        // Define headers
        const headers = [
            { header: 'Trailer', key: 'trailer', width: 20 },
            { header: 'Region', key: 'region', width: 15 },
            ...categories.map(category => ({
                header: category,
                key: category,
                width: 15
            })),
            { header: 'Total', key: 'total', width: 12 }
        ];
        worksheet.columns = headers;

        // Process data to map costs by trailer and category
        let trailerMap = new Map();
        data.forEach(item => {
            const trailerEntry = trailerMap.get(item.trailer_name) || {
                trailer: item.trailer_name,
                region: item.region,
                total: 0,
                ...Object.fromEntries(categories.map(category => [category, 0]))
            };

            trailerEntry[item.category_name] += parseFloat(
                item.invoice_total_cost
            );
            trailerEntry.total += parseFloat(item.invoice_total_cost);
            trailerMap.set(item.trailer_name, trailerEntry);
        });

        // Add rows to the worksheet
        trailerMap.forEach(entry => {
            worksheet.addRow(entry);
        });

        // Write and download the Excel file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type:
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'Annual_Spending_by_Category.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};
